import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  inject,
  Input,
  Output,
  EventEmitter
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { UmssIconComponent } from '../icon/icon.component';
import { UmssTooltipDirective } from '../../directives/tooltip.directive';
import { horizontalCollapseInOut } from '../../animations';

@Component({
  selector: 'umss-toggle-button',
  standalone: true,
  imports: [CommonModule, UmssIconComponent, UmssTooltipDirective],
  templateUrl: './toggle-button.component.html',
  styleUrls: ['./toggle-button.component.scss'],
  animations: [horizontalCollapseInOut]
})
export class UmssToggleButtonComponent {
  @HostBinding('style.display') display = 'contents';

  readonly elementRef = inject(ElementRef);
  /** Exposed so that parent button-group can trigger change detection on this
   *  component after mutating its @Input() properties programmatically.
   *  Projected content is owned by the host view, not button-group, so
   *  button-group's own detectChanges() doesn't reach these components. */
  readonly cdr = inject(ChangeDetectorRef);

  @Input() label: string = 'Toggle';
  @Input() value: unknown; // Value used by button-group for form binding
  @Input() leftIconName?: string; // Optional left Phosphor icon
  @Input() rightIconName?: string; // Optional right Phosphor icon

  /** Variant props - Well-typed and comprehensive */
  @Input() variant: 'outlined' | 'ghost' = 'outlined'; // Keep consistent with other components
  @Input() size: 'Mini' | 'Small' | 'Regular' | 'Large' = 'Regular'; // Match button component and Figma
  @Input() position: 'Left' | 'Middle' | 'Right' | 'Single' = 'Single'; // Button group position (affects border-radius)

  /** State props - Simple and clear */
  @Input() pressed: boolean = false; // Toggle state (replaces "state")
  @Input() disabled: boolean = false;
  @Input() checkmarkPosition: 'off' | 'left' | 'right' = 'off';
  @Input() title?: string;
  /** CSS grid-column span — set by parent button-group for balanced last-row filling */
  @Input() gridSpan: number = 1;
  /** Vertical position within a connected multi-row group (rowGap=0). 'only' = not connected. */
  @Input() rowPosition: 'only' | 'first' | 'middle' | 'last' = 'only';

  /** Events - Proper event handling */
  @Output() onToggle = new EventEmitter<boolean>(); // Emits the new pressed state
  @Output() onClick = new EventEmitter<MouseEvent>();

  onButtonClick(event: MouseEvent) {
    if (!this.disabled) {
      this.onToggle.emit(!this.pressed);
      this.onClick.emit(event);
    }
  }

  /** Computed properties */
  get computedAriaLabel(): string {
    return `${this.label} ${this.pressed ? 'pressed' : 'not pressed'}`;
  }

  get computedAriaPressed(): string {
    return this.pressed.toString();
  }

  get iconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '12px';
      case 'Small':
        return '14px';
      case 'Regular':
        return '16px';
      case 'Large':
        return '20px';
      default:
        return '16px';
    }
  }

  get checkmarkIconSize(): string {
    switch (this.size) {
      case 'Mini':
        return '10px';
      case 'Small':
        return '11px';
      case 'Regular':
        return '12px';
      case 'Large':
        return '14px';
      default:
        return '12px';
    }
  }
}
